import React, { useContext, useEffect, useRef, useState } from 'react';
import { AlertCircle, File, FileReport, Plus } from 'tabler-icons-react';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Anchor, Button } from '@mantine/core';
import { useModalState, usePaginationFilter } from '../../../../helpers/hooks';
import TableView from '../../../../components/common/TableView';
import { triggerNotification } from '../../../../helpers/notificationHelper';
import { Context as RegistrationAdminContext } from '../../../../providers/RegistrationAdminProvider';
import { Context as RegistrationAdminDashboardContext } from '../../../../providers/RegistrationAdminDashboardProvider';
import AdminView from '../../../../components/common/AdminView';
import AppCard from '../../../../components/common/AppCard';
import { useReportHistory } from '../../../../helpers/reportHelper';
import CustomReportModal from '../../../../components/content/reports/CustomReportModal';
import AppStack from '../../../../components/common/AppStack';
import AppText from '../../../../components/common/AppText';

const VIEW_ACTIONS_ENUM = {
  create: 'CREATE',
  edit: 'EDIT',
  delete: 'DELETE',
  generate: 'GENERATE'
};

const VIEW_ACTIONS = [
  {
    label: 'Generate',
    value: VIEW_ACTIONS_ENUM.generate
  },
  {
    label: 'Edit',
    value: VIEW_ACTIONS_ENUM.edit
  },
  {
    label: 'Delete',
    value: VIEW_ACTIONS_ENUM.delete
  }
];

const TABLE_COLUMNS = [
  {
    label: 'Title',
    value: 'name',
    sortable: true
  },
  {
    label: 'Form',
    value: 'form',
    sortable: true
  },
  {
    label: 'Last viewed',
    value: 'last_viewed',
    sortable: true
  }
];

const CustomReportListTableView = ({ baseUrl, ...rest }) => {
  const { reportHistory } = useReportHistory();
  const fetchedPkRegAssociation = useRef(null);
  const { state, fetchPaginatedCustomReports } = useContext(
    RegistrationAdminContext
  );
  const { state: dashboardState, changeReportViewSettings } = useContext(
    RegistrationAdminDashboardContext
  );
  const { state: modalState, onOpenModal, onCloseModal } = useModalState();
  const {
    pageIndex,
    totalCount,
    totalPages,
    totalNoFilterCount,
    data,
    filter,
    loading: dataLoading,
    isDescendingSort,
    onPageChange,
    onFilterChange,
    onSortChange,
    onRefresh
  } = usePaginationFilter(
    {
      search: '',
      status: '',
      sort: 'name'
    },
    (newFilter) => {
      fetchPaginatedCustomReports(
        dashboardState.regAssociation.value?.pkRegAssociation,
        newFilter,
        null,
        (error) => {
          triggerNotification(error);
        }
      );
    },
    state.paginatedRegAssociationCustomReports
  );

  const filterOptions = state.regAssociationSavedTemplates.value.reduce(
    (r, c) => {
      if (!r.regFormOptions.find((f) => f.value === c.regForm.uuid)) {
        r.regFormOptions.push({
          label: c.regForm.name,
          value: c.regForm.uuid
        });
      }
      return r;
    },
    { regFormOptions: [] }
  );

  const regAssociation = dashboardState.regAssociation.value;
  const loading =
    fetchedPkRegAssociation.current !== regAssociation?.pkRegAssociation ||
    dataLoading;

  useEffect(() => {
    if (
      regAssociation &&
      fetchedPkRegAssociation.current !== regAssociation.pkRegAssociation
    ) {
      onRefresh();
      fetchedPkRegAssociation.current = regAssociation.pkRegAssociation;
    }
  }, [regAssociation]);

  return (
    <AdminView
      customControl={
        <>
          <Button
            color="dark"
            onClick={() => {
              changeReportViewSettings({
                isClassicView: !dashboardState.view.isClassicReportView
              });
            }}
            variant="outline"
          >
            Legacy view
          </Button>

          <Button
            leftSection={<Plus />}
            onClick={() => {
              onOpenModal('CREATE');
            }}
          >
            Create Report
          </Button>
        </>
      }
      infoContent={
        <>
          <AppStack>
            <AlertCircle color="dodgerblue" size={22} />
          </AppStack>
          <AppText style={{ fontSize: 14 }}>
            You're currently experiencing the new reports interface. To return
            to the classic view,{' '}
            <Anchor
              color="dodgerblue"
              onClick={(e) => {
                e.preventDefault();
                changeReportViewSettings({isClassicView: !dashboardState.view.isClassicReportView});
              }}
              style={{ fontSize: 14 }}
            >
              click here.
            </Anchor>
          </AppText>
        </>
      }
      {...rest}
    >
      <AppCard
        style={{ flex: 1, padding: 0, height: '100%', borderTop: 'none' }}
        withBorder
      >
        <TableView
          columns={TABLE_COLUMNS}
          disabled={!loading && totalNoFilterCount === 0}
          disabledContent={{
            title: 'No registrations found.',
            description:
              'Registrations will appear here once players have registered.',
            icon: <File color="#000" size={125} />
          }}
          emptyMessage="No registrations found."
          enableInputsWhenLoading
          filters={[
            {
              key: 1,
              label: 'Search',
              placeholder: 'Filter by name',
              value: filter.search,
              onChange: (value) => onFilterChange({ ...filter, search: value })
            },
            {
              key: 2,
              label: 'Form',
              type: 'select',
              value: filter.regFormUuid,
              placeholder: 'Filter by form',
              data: filterOptions.regFormOptions.sort((a, b) =>
                a.label.localeCompare(b.label)
              ),
              clearable: true,
              onChange: (value) => {
                onFilterChange({ ...filter, regFormUuid: value });
              }
            }
          ]}
          isDescendingSort={isDescendingSort}
          isLoading={loading}
          noSort
          onAction={(action, item) => {
            onOpenModal(
              action,
              state.regAssociationSavedTemplates.value.find(
                (s) => s.pkRegAssociationSavedTemplate === item.key
              )
            );
          }}
          onChangeSortBy={onSortChange}
          onPageChange={onPageChange}
          onRefresh={onRefresh}
          pageIndex={pageIndex}
          rows={data.map((a) => {
            const viewHistory = reportHistory.reports.find(
              (f) => f.uuid === a.uuid
            );

            return {
              key: a.pkRegAssociationSavedTemplate,
              actions: VIEW_ACTIONS,
              columns: [
                {
                  key: 1,
                  label: a.name,
                  component: Link,
                  weight: 500,
                  subLabel: a.description,
                  icon: <FileReport color="black" size="30" />,
                  onClick: () => onOpenModal(VIEW_ACTIONS_ENUM.generate, a)
                },
                {
                  key: 2,
                  label: a.regForm.name
                },
                {
                  key: 3,
                  label: viewHistory
                    ? dayjs(viewHistory.date).format('MMM DD, YYYY')
                    : '',
                  sortValue: viewHistory ? new Date(viewHistory.date) : null,
                  sortValueType: 'date'
                }
              ]
            };
          })}
          selectInputOnly
          sortBy={filter.sort || TABLE_COLUMNS[0].value}
          tableTitle="Registrations"
          totalCount={totalCount}
          totalPages={totalPages}
        />
      </AppCard>

      <CustomReportModal
        generateSavedTemplate={modalState.action === VIEW_ACTIONS_ENUM.generate}
        isOpen={modalState.isOpen}
        onClose={onCloseModal}
        onSaveReportCallback={() => {
          onRefresh();
          onCloseModal();
        }}
        pkRegAssociation={regAssociation?.pkRegAssociation}
        regAssociationSavedTemplate={modalState.item}
        showDelete={modalState.action === VIEW_ACTIONS_ENUM.delete}
      />
    </AdminView>
  );
};

CustomReportListTableView.propTypes = { baseUrl: PropTypes.string };

export default CustomReportListTableView;
